"use client";

import React, { startTransition, useState } from "react";
import { getCasesAction } from "@/actions/search";
import { CaseData, CMSSection } from "@/cms/models";
import PropertyGalleryCard from "./PropertyGalleryCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/sharp-solid-svg-icons";
import { motion, useAnimation } from "framer-motion";
import Card from "./ui/Card";
import RippleCtaButton from "./ui/RippleCtaButton";
export interface CaseListViewerProps {
  cases: CaseData[];
  totalCount: number;
  initialFilter: string;
  filters: { id: string; title: string; label: string; emptyText: string }[];
  cmsSection: CMSSection<any>;
  resourceGroupIdFilter?: string;
}

const CaseListViewer: React.FC<CaseListViewerProps> = ({
  cases,
  totalCount,
  initialFilter,
  filters,
  cmsSection,
  resourceGroupIdFilter,
}) => {
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const [take, setTake] = useState(3);
  const [total, setTotal] = useState(totalCount);

  const [results, setResults] = useState<CaseData[]>(cases);

  const getCases = async (filter: string, caseCount: number) => {
    setSelectedFilter(filter);

    startTransition(async () => {
      const getResponse = await getCasesAction({
        sold: filter === "latest_sold" || filter === "latest_rented",
        hasUpcomingOpenhouse: filter === "openhouse",
        isRental: filter === "latest_rented",
        resourceGroupIdFilter: resourceGroupIdFilter,
        take: caseCount,
      });

      const caseList =
        getResponse.items?.map((x) => ({
          ...x,
          hasUpcomingOpenhouse: filter === "openhouse",
        })) ?? [];

      setResults(caseList);
      setTotal(getResponse.totalCount ?? 0);
    });
  };

  const controls = useAnimation();
  return (
    <div className="flex flex-col gap-4 flex-1 w-full">
      <div className="flex flex-col lg:flex-row justify-between gap-2">
        <div
          style={{ color: cmsSection?.specifics?.textColor }}
          className="homepage-text-xl text-[var(--page-text-color)]"
        >
          {filters.find((x) => x.id == selectedFilter)?.title}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {filters.length > 1 &&
            filters.map((filter) => (
              <RippleCtaButton
                key={filter.id}
                isActive={selectedFilter == filter.id}
                onClick={async () => await getCases(filter.id, take)}
              >
                {filter.label}
              </RippleCtaButton>
            ))}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((caseData, index) => (
              <motion.div
                key={`${caseData.id}`}
                initial="initial"
                whileInView="animate"
                animate={controls}
                viewport={{
                  once: true,
                }}
                custom={index}
                variants={{
                  initial: {
                    opacity: 0,
                    y: 100,
                  },
                  animate: (index: number) => ({
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.05 * index,
                    },
                  }),
                }}
              >
                <PropertyGalleryCard property={caseData} shadow="none" />
              </motion.div>
            ))}
          </div>
        )}

        {results.length === 0 && (
          <Card>
            <div
              style={{ color: cmsSection?.specifics?.textColor }}
              className="homepage-text-sm p-4"
            >
              {filters.find((x) => x.id == selectedFilter)?.emptyText}
            </div>
          </Card>
        )}
        {total > results.length && (
          <div className="flex justify-center">
            <RippleCtaButton
              onClick={async () => {
                const newTake = take + 3;
                setTake(newTake);
                await getCases(selectedFilter, newTake);
              }}
            >
              <span className="flex gap-2 items-center justify-center">
                <FontAwesomeIcon className="size-4" icon={faCirclePlus} />
                Se flere
              </span>
            </RippleCtaButton>
          </div>
        )}
      </div>
    </div>
  );
};
export default CaseListViewer;
